import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getSettings,
  updateSettings,
} from "../../../services/settingsServices";
import { getAllGalleries } from "../../../services/gallerieServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  FaSave,
  FaChevronLeft,
  FaChevronRight,
  FaMoon,
  FaSun,
  FaPlay,
  FaPause,
  FaUpload,
  FaTimes,
} from "react-icons/fa";
import HomeSectionsManager from "./HomeSectionsManager";
import {
  uploadSettingsHeader1,
  uploadSettingsHeader2,
  toAbsolutePhotoUrl,
} from "../../../services/settingsPhotoServices";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";

/* Quill */
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
};
const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

/* Helpers Preview */
const getTextAlignment = (align) => {
  switch (align) {
    case "left":
      return "text-left left-5 sm:left-10";
    case "right":
      return "text-right right-5 sm:right-10";
    default:
      return "";
  }
};

const HomeSettingsForm = () => {
  const [settings, setSettings] = useState({
    header_url1: "",
    header_texte1: "",
    header_url2: "",
    header_texte2: "",
    about_title: "",
    about_content: "",
    carrousel_id: "",
    logo_club: "",
  });

  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Preview state
  const [previewDark, setPreviewDark] = useState(false);
  const [slide, setSlide] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const intervalRef = useRef(null);

  // Cache-busting (pour forcer le refresh après upload)
  const withBust = (url, v) =>
    url ? `${url}${url.includes("?") ? "&" : "?"}v=${v}` : url;

  // Modale upload (Header 1 / Header 2)
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null); // "header1" | "header2"
  const [selectedFile, setSelectedFile] = useState(null);

  const [outputFormat, setOutputFormat] = useState("webp"); // webp|jpg|png
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // version par image pour cache-busting
  const [imgVersion, setImgVersion] = useState({ header1: 0, header2: 0 });

  const uploadDialogRef = useRef(null);

  const headerImages = useMemo(() => {
    const src1Raw =
      settings.header_url1?.trim() || "/img/no-image.jpg";
    const src2Raw =
      settings.header_url2?.trim() || "/img/no-image.jpg";

    const src1Abs = toAbsolutePhotoUrl(src1Raw);
    const src2Abs = toAbsolutePhotoUrl(src2Raw);

    return [
      {
        src: withBust(
          src1Abs || "/img/no-image.jpg",
          imgVersion.header1,
        ),
        text: settings.header_texte1 || "",
        align: "left",
      },
      {
        src: withBust(
          src2Abs || "/img/no-image.jpg",
          imgVersion.header2,
        ),
        text: settings.header_texte2 || "",
        align: "right",
      },
    ];
  }, [
    settings.header_url1,
    settings.header_texte1,
    settings.header_url2,
    settings.header_texte2,
    imgVersion.header1,
    imgVersion.header2,
  ]);

  const selectedGallery = useMemo(() => {
    if (!settings.carrousel_id) return null;
    return (galleries || []).find(
      (g) => String(g.id) === String(settings.carrousel_id),
    );
  }, [galleries, settings.carrousel_id]);

  const carrouselPhotos = useMemo(() => {
    const list = selectedGallery?.photos || [];
    return list.filter((p) => !!p.is_active).slice(0, 12); // limite à 12 pour l’aperçu
  }, [selectedGallery]);

  useEffect(() => {
    const fetchSettingsAndGalleries = async () => {
      try {
        const [settingsData, galleriesData] = await Promise.all([
          getSettings(),
          getAllGalleries(),
        ]);
        if (Array.isArray(settingsData) && settingsData.length > 0) {
          setSettings((prev) => ({ ...prev, ...settingsData[0] }));
        }
        setGalleries(galleriesData || []);
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
        toast.error("Impossible de charger les paramètres.");
      } finally {
        setLoading(false);
      }
    };
    fetchSettingsAndGalleries();
  }, []);

  // Auto play slider (aperçu)
  useEffect(() => {
    if (!autoPlay) return;
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setSlide((s) => (s + 1) % headerImages.length);
    }, 8000);
    return () => clearInterval(intervalRef.current);
  }, [autoPlay, headerImages.length]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContentChange = (name, value) => {
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...settings,
        carrousel_id: settings.carrousel_id || null,
      };
      await updateSettings(payload);
      toast.success("Paramètres mis à jour avec succès");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour des paramètres");
    }
  };

  const openUpload = (target) => {
    setUploadTarget(target); // "header1" | "header2"
    setSelectedFile(null);
    setUploadProgress(0);
    setOutputFormat("webp");
    setIsUploadOpen(true);
  };

  const closeUpload = () => {
    if (uploading) return; // évite fermeture pendant upload
    setIsUploadOpen(false);
    setUploadTarget(null);
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setSelectedFile(f);

    if (!f) return;

    // Check type
    const okType = /^image\/(png|jpe?g|webp)$/i.test(f.type);
    if (!okType) {
      toast.error("Format non supporté. Utilisez PNG / JPG / WEBP.");
      return;
    }

    // Check size
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      return;
    }

    // ✅ warning dimensions (non bloquant)
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => {
      if (img.width < 1600) {
        toast.info("Conseil : image ≥ 1600px de large pour un header net.");
      }
      if (img.height > img.width) {
        toast.info("Conseil : privilégiez une image en format paysage.");
      }
      URL.revokeObjectURL(url);
    };
    img.onerror = () => URL.revokeObjectURL(url);
    img.src = url;
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();

    if (!uploadTarget) {
      toast.error("Cible d’upload inconnue.");
      return;
    }
    if (!selectedFile) {
      toast.warn("Sélectionnez une image.");
      return;
    }

    const okType = /^image\/(png|jpe?g|webp)$/i.test(selectedFile.type);
    if (!okType) {
      toast.error("Format non supporté. Utilisez PNG / JPG / WEBP.");
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const uploadFn =
        uploadTarget === "header1"
          ? uploadSettingsHeader1
          : uploadSettingsHeader2;

      const updated = await uploadFn(selectedFile, {
        format: outputFormat,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const p = Math.round((evt.loaded / evt.total) * 100);
          setUploadProgress(p);
        },
      });

      // compat back : { url } ou { imageUrl }
      const newUrl = updated?.url || updated?.imageUrl || "";

      if (!newUrl) {
        toast.error("Réponse upload invalide (url manquante).");
        return;
      }

      if (uploadTarget === "header1") {
        setSettings((p) => ({ ...p, header_url1: newUrl }));
        setImgVersion((v) => ({ ...v, header1: v.header1 + 1 }));
      } else {
        setSettings((p) => ({ ...p, header_url2: newUrl }));
        setImgVersion((v) => ({ ...v, header2: v.header2 + 1 }));
      }

      toast.success("Image du header mise à jour !");
      closeUpload();
    } catch (err) {
      console.error(
        "[HomeSettingsForm] upload header:",
        err?.response?.data || err,
      );
      toast.error("Erreur lors du téléversement de l’image.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (loading) {
    return (
      <section className="w-full">
        <div className="mx-auto max-w-7xl px-1 sm:px-1">
          <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8">
              <div className="h-8 w-64 bg-white/25 rounded animate-pulse" />
            </div>
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-24 rounded-xl bg-slate-100/80 dark:bg-slate-800/40 animate-pulse"
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-1 sm:px-1">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          {/* HEADER */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                Page d’Accueil — Paramètres & Prévisualisation
              </h2>
              <p className="text-white/90 text-sm sm:text-[15px] mt-1">
                Modifiez le header, la section « À propos » et l’album
                carrousel, tout en visualisant le rendu.
              </p>
            </div>
            <Button
              text="Enregistrer"
              icon={FaSave}
              onClick={handleSubmit}
              className="!bg-white !text-slate-900 !hover:bg-white/90 !shadow-md !rounded-xl gap-2"
            />
          </div>

          {/* CONTENT: Form + Preview */}
          <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Formulaire */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Header images */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-3">
                  Header — Image 1
                </h3>
                <div className="mt-3 flex items-start gap-3">
                  <div className="h-24 w-44 rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700 bg-slate-100 dark:bg-slate-800">
                    <img
                      src={headerImages[0].src}
                      alt="Header 1"
                      className="h-full w-full object-cover"
                      onError={(e) =>
                        (e.currentTarget.src = "/img/no-image.jpg")
                      }
                    />
                  </div>

                  <div className="flex-1">
                    <Button
                      type="button"
                      text="Téléverser une image"
                      icon={FaUpload}
                      className="!rounded-xl gap-2"
                      onClick={() => openUpload("header1")}
                    />
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      Recommandé : format paysage, largeur ≥ 1600px, JPG/WEBP.
                      Évite les portraits.
                    </p>
                  </div>
                </div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Lien de l’image
                </label>
                <input
                  type="url"
                  name="header_url1"
                  value={settings.header_url1}
                  onChange={handleChange}
                  placeholder="https://…"
                  inputMode="url"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                <label className="mt-4 block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Texte superposé
                </label>

                <div
                  className="mt-1
                  [&_.ql-toolbar]:rounded-t-xl [&_.ql-toolbar]:border-slate-300 dark:[&_.ql-toolbar]:border-slate-700
                  [&_.ql-toolbar]:bg-white/80 dark:[&_.ql-toolbar]:bg-slate-900/70
                  [&_.ql-container]:rounded-b-xl [&_.ql-container]:border-slate-300 dark:[&_.ql-container]:border-slate-700
                  [&_.ql-container]:bg-white/80 dark:[&_.ql-container]:bg-slate-900/70"
                >
                  <ReactQuill
                    value={settings.header_texte1}
                    onChange={(v) => handleContentChange("header_texte1", v)}
                    modules={modules}
                    formats={formats}
                    placeholder="<h1>Votre titre</h1><p>Votre sous-titre</p>"
                  />
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-3">
                  Header — Image 2
                </h3>
                <div className="mt-3 flex items-start gap-3">
                  <div className="h-24 w-44 rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700 bg-slate-100 dark:bg-slate-800">
                    <img
                      src={headerImages[1].src}
                      alt="Header 2"
                      className="h-full w-full object-cover"
                      onError={(e) =>
                        (e.currentTarget.src = "/img/header_placeholder_2.jpg")
                      }
                    />
                  </div>

                  <div className="flex-1">
                    <Button
                      type="button"
                      text="Téléverser une image"
                      icon={FaUpload}
                      className="!rounded-xl gap-2"
                      onClick={() => openUpload("header2")}
                    />
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      Recommandé : format paysage, largeur ≥ 1600px, JPG/WEBP.
                    </p>
                  </div>
                </div>

                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Lien de l’image
                </label>
                <input
                  type="url"
                  name="header_url2"
                  value={settings.header_url2}
                  onChange={handleChange}
                  placeholder="https://…"
                  inputMode="url"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                <label className="mt-4 block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Texte superposé
                </label>
                <div
                  className="mt-1
                  [&_.ql-toolbar]:rounded-t-xl [&_.ql-toolbar]:border-slate-300 dark:[&_.ql-toolbar]:border-slate-700
                  [&_.ql-toolbar]:bg-white/80 dark:[&_.ql-toolbar]:bg-slate-900/70
                  [&_.ql-container]:rounded-b-xl [&_.ql-container]:border-slate-300 dark:[&_.ql-container]:border-slate-700
                  [&_.ql-container]:bg-white/80 dark:[&_.ql-container]:bg-slate-900/70"
                >
                  <ReactQuill
                    value={settings.header_texte2}
                    onChange={(v) => handleContentChange("header_texte2", v)}
                    modules={modules}
                    formats={formats}
                    placeholder="<h1>Votre titre</h1><p>Votre sous-titre</p>"
                  />
                </div>
              </div>

              {/* À propos */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-3">
                  Section « À propos »
                </h3>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Titre
                </label>
                <input
                  type="text"
                  name="about_title"
                  value={settings.about_title}
                  onChange={handleChange}
                  placeholder="Qui sommes-nous ?"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                <label className="mt-4 block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Contenu
                </label>
                <div
                  className="mt-1
                  [&_.ql-toolbar]:rounded-t-xl [&_.ql-toolbar]:border-slate-300 dark:[&_.ql-toolbar]:border-slate-700
                  [&_.ql-toolbar]:bg-white/80 dark:[&_.ql-toolbar]:bg-slate-900/70
                  [&_.ql-container]:rounded-b-xl [&_.ql-container]:border-slate-300 dark:[&_.ql-container]:border-slate-700
                  [&_.ql-container]:bg-white/80 dark:[&_.ql-container]:bg-slate-900/70"
                >
                  <ReactQuill
                    value={settings.about_content}
                    onChange={(v) => handleContentChange("about_content", v)}
                    modules={modules}
                    formats={formats}
                    placeholder="<p>Notre club...</p>"
                  />
                </div>
              </div>

              {/* Carrousel */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-3">
                  Carrousel (album photo)
                </h3>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Sélection de l’album
                </label>
                {galleries.length > 0 ? (
                  <select
                    name="carrousel_id"
                    value={settings.carrousel_id || ""}
                    onChange={(e) => {
                      const value =
                        e.target.value === "" ? null : e.target.value;
                      setSettings((prev) => ({ ...prev, carrousel_id: value }));
                    }}
                    className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  >
                    <option value="">❌ Ne pas afficher d’album</option>
                    {galleries.map((gallery) => (
                      <option key={gallery.id} value={gallery.id}>
                        {gallery.titre}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="mt-1 text-sm text-slate-500">
                    Vous n’avez pas encore d’album photo.
                  </p>
                )}
                {settings.carrousel_id && (
                  <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                    Album sélectionné :{" "}
                    <span className="font-semibold">
                      {galleries.find(
                        (g) => String(g.id) === String(settings.carrousel_id),
                      )?.titre || `#${settings.carrousel_id}`}
                    </span>
                  </p>
                )}
              </div>

              {/* Actions bas */}
              <div className="flex justify-end">
                <Button
                  text="Mettre à jour"
                  type="submit"
                  className="!rounded-xl gap-2"
                  icon={FaSave}
                />
              </div>
            </form>

            {/* Prévisualisation temps réel */}
            <div className="space-y-6">
              {/* Toggle Dark/Light + autoplay */}
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setAutoPlay((v) => !v)}
                  className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white/70 dark:bg-slate-900/70 px-3 py-2 text-sm inline-flex items-center gap-2 hover:bg-white/90 dark:hover:bg-slate-800"
                  title={autoPlay ? "Mettre en pause" : "Lancer le défilement"}
                >
                  {autoPlay ? <FaPause /> : <FaPlay />}
                  {autoPlay ? "Pause" : "Lecture"}
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewDark((d) => !d)}
                  className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white/70 dark:bg-slate-900/70 px-3 py-2 text-sm inline-flex items-center gap-2 hover:bg-white/90 dark:hover:bg-slate-800"
                >
                  {previewDark ? <FaSun /> : <FaMoon />}
                  {previewDark ? "Clair" : "Sombre"}
                </button>
              </div>

              {/* HEADER PREVIEW (plus proche HeaderSlider) */}
              <div className={`${previewDark ? "dark" : ""}`}>
                <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40">
                  <div className="relative w-full h-[260px] sm:h-[320px] bg-slate-200 dark:bg-slate-800">
                    {/* Slides */}
                    {headerImages.map((s, i) => (
                      <div
                        key={i}
                        className={`absolute inset-0 transition-opacity duration-700 ${
                          i === slide ? "opacity-100" : "opacity-0"
                        }`}
                        aria-hidden={i !== slide}
                      >
                        <img
                          src={s.src}
                          alt={`slide-${i}`}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src =
                              i === 0
                                ? "/img/no-image.jpg"
                                : "/img/no-image.jpg";
                          }}
                        />

                        {/* Overlay comme ton HeaderSlider (gauche/droite) */}
                        <div
                          className={`absolute inset-0 ${
                            s.align === "right"
                              ? "bg-gradient-to-l from-slate-900/65 via-slate-900/35 to-transparent"
                              : "bg-gradient-to-r from-slate-900/65 via-slate-900/35 to-transparent"
                          }`}
                        />
                      </div>
                    ))}

                    {/* Texte (align comme HeaderSlider) */}
                    <div
                      className={`absolute inset-0 flex items-center ${
                        headerImages[slide].align === "right"
                          ? "justify-end text-right"
                          : "justify-start text-left"
                      }`}
                    >
                      <div
                        className={`max-w-[80%] sm:max-w-[60%] px-5 sm:px-10 text-white drop-shadow-xl`}
                      >
                        <div
                          className="animate-fadein prose prose-invert max-w-none"
                          dangerouslySetInnerHTML={{
                            __html: headerImages[slide].text,
                          }}
                        />
                      </div>
                    </div>

                    {/* Controls (comme HeaderSlider) */}
                    <button
                      type="button"
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-slate-900/40 hover:bg-slate-900/60 text-white rounded-full p-2"
                      onClick={() =>
                        setSlide(
                          (s) =>
                            (s - 1 + headerImages.length) % headerImages.length,
                        )
                      }
                      title="Précédent"
                    >
                      <FaChevronLeft />
                    </button>
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-slate-900/40 hover:bg-slate-900/60 text-white rounded-full p-2"
                      onClick={() =>
                        setSlide((s) => (s + 1) % headerImages.length)
                      }
                      title="Suivant"
                    >
                      <FaChevronRight />
                    </button>

                    {/* Dots */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                      {headerImages.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setSlide(i)}
                          className={`h-2.5 w-2.5 rounded-full border border-white/70 ${
                            i === slide ? "bg-white" : "bg-transparent"
                          }`}
                          aria-label={`Aller au visuel ${i + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* ABOUT PREVIEW */}
              <div className={`${previewDark ? "dark" : ""}`}>
                <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-5">
                  <section className="py-6 bg-gray-100 dark:bg-gray-700 dark:text-white rounded-xl">
                    <div className="container mx-auto px-4">
                      <div className="flex flex-wrap -mx-4 overflow-hidden items-center">
                        <div className="px-4 w-full lg:w-1/3 overflow-hidden my-4">
                          <img
                            src={
                              toAbsolutePhotoUrl(
                                settings.logo_club?.trim() || "",
                              ) || "/img/no-image.jpg"
                            }
                            alt="Logo Club"
                            className="block mx-auto drop-shadow-md max-h-40 object-contain"
                            onError={(e) =>
                              (e.currentTarget.src = "/img/no-image.jpg")
                            }
                          />
                        </div>
                        <div className="font-montserrat px-4 lg:px-8 w-full lg:w-2/3 overflow-hidden my-4">
                          <h2 className="text-2xl sm:text-3xl font-extrabold mb-4">
                            {settings.about_title || "À propos de notre club"}
                          </h2>
                          <div
                            className="text-justify mb-2 leading-relaxed about_content prose prose-slate dark:prose-invert max-w-none"
                            dangerouslySetInnerHTML={{
                              __html:
                                settings.about_content ||
                                "<p>Ajoutez votre présentation ici.</p>",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>

              {/* CAROUSEL PREVIEW (mini diaporama) */}
              <div className={`${previewDark ? "dark" : ""}`}>
                <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-5">
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                    Carrousel (aperçu)
                  </h4>

                  {settings.carrousel_id ? (
                    <>
                      <div className="text-sm text-slate-700 dark:text-slate-300">
                        Album sélectionné :{" "}
                        <span className="inline-flex items-center gap-2 rounded-lg px-2 py-1 bg-slate-100 dark:bg-slate-800">
                          <span className="h-2 w-2 rounded-full bg-violet-500 inline-block" />
                          {selectedGallery?.titre ||
                            `#${settings.carrousel_id}`}
                        </span>
                      </div>

                      {carrouselPhotos.length > 0 ? (
                        <div className="mt-3 home-settings-carousel rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-slate-100 dark:bg-slate-900">
                          <Carousel
                            autoPlay={carrouselPhotos.length > 1}
                            infiniteLoop={carrouselPhotos.length > 1}
                            useKeyboardArrows
                            showThumbs={false}
                            showStatus={false}
                            showIndicators={carrouselPhotos.length > 1}
                            showArrows={carrouselPhotos.length > 1}
                            interval={4500}
                            stopOnHover
                            swipeable
                            emulateTouch
                            dynamicHeight={false}
                          >
                            {carrouselPhotos.map((photo) => (
                              <div key={photo.id} className="hs-slide">
                                <img
                                  src={toAbsolutePhotoUrl(photo.photo_url)}
                                  alt={photo.description || "Photo"}
                                  onError={(e) => {
                                    e.currentTarget.src = "/img/no-image.jpg";
                                  }}
                                />
                              </div>
                            ))}
                          </Carousel>

                          <div className="px-3 py-2 text-[11px] text-slate-500 dark:text-slate-400 border-t border-slate-200/70 dark:border-slate-800/70">
                            Aperçu réduit (les images exactes s’afficheront sur
                            la page d’accueil).
                          </div>
                        </div>
                      ) : (
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                          Cet album n’a pas (encore) de photos actives.
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-slate-500">
                      Aucun album sélectionné.
                    </p>
                  )}
                </div>

                {/* Styles “mini” pour le carousel dans le panneau admin */}
                <style>{`
    .home-settings-carousel .carousel .slide {
      background: transparent !important;
    }
    .home-settings-carousel .carousel .slide img {
      height: 160px;            /* ✅ taille réduite */
      width: 100%;
      object-fit: cover;        /* ✅ rendu “vitrine” */
      display: block;
    }
    .home-settings-carousel .carousel .control-dots {
      margin: 0 0 6px 0;
    }
  `}</style>
              </div>
            </div>
          </div>
        </div>
      </div>

      <HomeSectionsManager />

      {/* Styles locaux pour l’aperçu (animations légères) */}
      <style>{`
        .animate-fadein { animation: fadein .5s ease-out both; }
        @keyframes fadein { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: translateY(0) } }

        /* Placeholders */
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }

        /* Apple-friendly text smoothing */
        html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
      `}</style>

      {/* Upload Modal Header 1/2 */}
      {isUploadOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeUpload();
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Téléverser une image de header"
        >
          <div
            ref={uploadDialogRef}
            className="relative w-full max-w-lg overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
              <div>
                <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
                  {uploadTarget === "header1"
                    ? "Téléverser — Header Image 1"
                    : "Téléverser — Header Image 2"}
                </h3>
                <p className="text-white/90 text-xs sm:text-sm mt-1">
                  Recommandé : paysage, ≥ 1600px de large.
                </p>
              </div>

              <button
                type="button"
                onClick={closeUpload}
                disabled={uploading}
                className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white disabled:opacity-60"
                title="Fermer"
              >
                <FaTimes />
              </button>
            </div>

            <form
              onSubmit={handleUploadSubmit}
              className="p-4 sm:p-6 space-y-4"
            >
              <input
                type="file"
                onChange={onFileChange}
                accept="image/png,image/jpeg,image/webp"
                className="w-full"
                disabled={uploading}
              />

              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-700 dark:text-slate-200">
                  Format
                </span>
                <select
                  className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-2 py-1 text-sm"
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value)}
                  disabled={uploading}
                >
                  <option value="webp">WEBP (recommandé)</option>
                  <option value="jpg">JPG</option>
                  <option value="png">PNG</option>
                </select>
              </div>

              {uploading && (
                <div>
                  <progress
                    className="progress w-full"
                    value={uploadProgress}
                    max="100"
                  />
                  <div className="text-xs mt-1">{uploadProgress}%</div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeUpload}
                  disabled={uploading}
                  className="rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  title="Annuler"
                >
                  <FaTimes /> Fermer
                </button>

                <Button
                  text={uploading ? "Transfert…" : "Sauvegarder"}
                  type="submit"
                  icon={FaSave}
                  className="!rounded-xl gap-2"
                  isLoading={uploading}
                  disabled={uploading || !selectedFile}
                />
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default HomeSettingsForm;
